import { edmCampaign } from '@bingads-webui/edm-campaign';
import { curlyTemplate } from '@bingads-webui/primitive-utilities';
import { EXPERIMENT_API_ERRORS } from '../common/constants/experiment-errors';

export const USE_SEARCH_MEASUREMENT = 1;
export const USE_VISIT_MEASUREMENT = 2;
export const USE_CONVERSION_MEASUREMENT = 4;

export const createMeasurementTypeEnum = ({
  useConversionLift,
  useSearchLift,
  useVisitLift,
}) => {
  const x = useSearchLift ? USE_SEARCH_MEASUREMENT : 0;
  const y = useVisitLift ? USE_VISIT_MEASUREMENT : 0;
  const z = useConversionLift ? USE_CONVERSION_MEASUREMENT : 0;
  return x + y + z;
};

export const parseMeasurementTypeEnum = combinedEnum => ({
  useConversionLift: combinedEnum.includes('Conversion'),
  useSearchLift: combinedEnum.includes('Search'),
  useVisitLift: combinedEnum.includes('Visit'),
});

export const stitchFullCampaignsIntoExperiments = (associatedCampaigns, fetchedLiftExperiments) => {
  const associatedCampaignsById = {};
  associatedCampaigns.forEach((campaign) => {
    associatedCampaignsById[campaign.Id] = campaign;
  });
  const formattedLiftExperiments = fetchedLiftExperiments.reduce((accum, liftExperiment) => {
    const formattedCampaigns = liftExperiment.Campaigns.map(campaignId => associatedCampaignsById[campaignId]);
    accum.push({
      ...liftExperiment,
      Campaigns: formattedCampaigns,
    });
    return accum;
  }, []);
  return formattedLiftExperiments;
};

export const fetchExperiment = async ({ cid, aid, liftExperimentId }, { onSuccess, onFailure }) => edmCampaign
  .Customers.$withKey(cid)
  .Accounts.$withKey(aid)
  .LiftExperiments
  .$withKey(liftExperimentId)
  .get({
    select: [
      'Id',
      'Name',
      'Campaigns',
      'Country',
      'Keywords',
      'MeasurementType',
      'StartDate',
      'SearchCustomerId',
      'UETTags',
    ],
  })
  .toPromise()
  .then((response) => {
    // This handles the case where a lift exp id param is included but is incorrect (the api returns a 204)
    if (!response) {
      throw new Error();
    }
    onSuccess(response);
  })
  .catch(() => onFailure(curlyTemplate(_TL_('Failed to fetch lift study: {{liftExperimentId}}'))({ liftExperimentId })));

export const fetchExperiments = ({ cid, aid }, { query, onSuccess, onFailure }) => edmCampaign
  .Customers.$withKey(cid)
  .Accounts.$withKey(aid)
  .LiftExperiments
  .get(query)
  .toPromise()
  .then((response) => {
    onSuccess(response.value);
  })
  .catch(() => onFailure(_TL_('Failed to fetch lift studies')));

export const saveExperiment = ({ cid, aid }, { payload, onSuccess, onFailure }) => edmCampaign
  .Customers.$withKey(cid)
  .Accounts.$withKey(aid)
  .LiftExperiments
  .post(payload)
  .toPromise()
  .then((response) => {
    onSuccess(response);
    return { isAPISaveFailure: false };
  })
  .catch((response) => {
    const apiErrorCode = response.body.value[0].Code;
    const errorMessage = curlyTemplate(_TL_('Failed to create lift study: {{errorMessage}}'))({ errorMessage: EXPERIMENT_API_ERRORS[apiErrorCode] });
    onFailure(errorMessage);
    return {
      isAPISaveFailure: true,
      error: new Error(errorMessage),
    };
  });


export const updateExperiment = ({ cid, aid }, { payload, onSuccess, onFailure }) => edmCampaign
  .Customers.$withKey(cid)
  .Accounts.$withKey(aid)
  .LiftExperiments
  .update(payload.Id, payload)
  .then((response) => {
    onSuccess(response);
    return { isAPISaveFailure: false };
  })
  .catch((response) => {
    const apiErrorCode = response.body.value[0].Code;
    const errorMessage = curlyTemplate(_TL_('Failed to update lift study: {{liftExperimentId}}. {{errorMessage}}'))({ errorMessage: EXPERIMENT_API_ERRORS[apiErrorCode], liftExperimentId: payload.Id });
    onFailure(errorMessage);
    return {
      isAPISaveFailure: true,
      error: new Error(errorMessage),
    };
  });

export const deleteExperiment = ({ cid, aid }, liftExperimentId, onSuccess, onFailure) => edmCampaign
  .Customers.$withKey(cid)
  .Accounts.$withKey(aid)
  .LiftExperiments
  .$withKey(liftExperimentId)
  .destroyAsPromise()
  .then(() => {
    onSuccess(liftExperimentId);
  })
  .catch(() => {
    onFailure(curlyTemplate(_TL_('Failed to delete lift study: {{liftExperimentId}}'))({ liftExperimentId }));
  });
